import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useScreenTime } from "@/hooks/useScreenTime";
import { Mail, Download, Loader2, ChevronDown } from "lucide-react";
import { useAppSetting, getBrandingUrl } from "@/hooks/useAppSettings";
import { useText } from "@/contexts/AppConfigContext";
import defaultLogo from "@/assets/app-icon.png";
import InstallGuideModal, { isIosSafari, isStandalone } from "@/components/InstallGuideModal";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES, LANGUAGE_LABELS, type SupportedLanguage } from "@/i18n";
import { useLocale } from "@/contexts/LocaleContext";

const getGlobalPrompt = () => (window as any).__pwaInstallPrompt;

const LANGUAGE_FLAGS: Record<SupportedLanguage, string> = {
  en: "🇺🇸",
  es: "🇲🇽",
  de: "🇩🇪",
};

const Login = () => {
  useScreenTime("login");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { language, setLanguage } = useLocale();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(getGlobalPrompt());
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const { data: logoPath } = useAppSetting("logo");
  const logoUrl = getBrandingUrl(logoPath) || defaultLogo;
  const appName = useText("app_name");

  useEffect(() => {
    if (getGlobalPrompt() && !deferredPrompt) {
      setDeferredPrompt(getGlobalPrompt());
    }
    const handler = (e: Event) => {
      e.preventDefault();
      (window as any).__pwaInstallPrompt = e;
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    if (isIosSafari() && !isStandalone()) {
      // Mostra o guia automaticamente após 2 segundos
      const timer = setTimeout(() => {
        setShowInstallGuide(true);
        localStorage.setItem("install_guide_shown", "true");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleInstallClick = async () => {
    const prompt = deferredPrompt || getGlobalPrompt();
    if (prompt) {
      try {
        prompt.prompt();
        const result = await prompt.userChoice;
        if (result.outcome === "accepted") {
          (window as any).__pwaInstallPrompt = null;
          setDeferredPrompt(null);
        }
      } catch {
        (window as any).__pwaInstallPrompt = null;
        setDeferredPrompt(null);
      }
    } else {
      setShowInstallGuide(true);
    }
  };

  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setError(t("login.email_invalid"));
      return;
    }
    setSubmitting(true);
    setError("");
    const result = await login(email);
    setSubmitting(false);
    if (result.error) {
      setError(result.error);
    } else {
      const redirectTo = searchParams.get("redirect") || "/";
      navigate(redirectTo, { replace: true });
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-5 py-8"
      style={{
        background: "linear-gradient(160deg, #faf6f2 0%, #f5ede6 30%, #f0e8df 60%, #ebe2d8 100%)",
      }}
    >
      <div className="w-full max-w-sm flex-1 flex flex-col justify-center animate-fade-in">
        {/* Language selector — top, shows all languages at once */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value as SupportedLanguage)}
              className="appearance-none pl-5 pr-11 py-2.5 rounded-full bg-card/80 backdrop-blur border border-border text-foreground text-sm font-bold shadow-sm hover:bg-card focus:outline-none focus:ring-2 focus:ring-primary/40 cursor-pointer transition-all"
              aria-label="Language"
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>
                  {LANGUAGE_FLAGS[lang]} {LANGUAGE_LABELS[lang]}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
          </div>
        </div>

        {/* Single unified card */}
        <div className="bg-card rounded-3xl shadow-[0_8px_40px_rgba(0,0,0,0.08)] p-8 flex flex-col items-center">
          {/* Logo */}
          <img
            src={logoUrl}
            alt={appName}
            className="w-24 h-24 object-contain mb-3 drop-shadow-sm"
          />

          {/* App name */}
          <h1 className="text-3xl font-extrabold text-foreground tracking-tight mb-6">
            {appName}
          </h1>

          {/* Login form */}
          <form onSubmit={handleSubmit} className="w-full space-y-3.5">
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type="email"
                placeholder={t("login.email_placeholder")}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-secondary/50 border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/50 text-base transition-all"
                autoFocus
                disabled={submitting}
              />
            </div>
            {error && <p className="text-red-500 text-sm font-medium px-1">{error}</p>}

            {/* Install — primary-colored action, above Sign In */}
            <button
              type="button"
              onClick={handleInstallClick}
              className="w-full py-3.5 rounded-2xl font-bold text-base transition-all active:scale-[0.97] flex items-center justify-center gap-2 text-white shadow-md shadow-primary/20"
              style={{ background: "linear-gradient(135deg, #E8601C 0%, #F5A623 100%)" }}
            >
              <Download size={18} />
              {t("login.install_button")}
            </button>

            {/* Sign In — neutral secondary action */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 rounded-2xl font-bold text-base transition-all active:scale-[0.97] disabled:opacity-60 flex items-center justify-center gap-2 bg-secondary text-secondary-foreground"
            >
              {submitting ? <><Loader2 size={18} className="animate-spin" /> {t("login.loading")}</> : t("login.button")}
            </button>
          </form>
        </div>
      </div>

      {/* Terms — discreet, page footer */}
      <p className="text-[11px] text-muted-foreground/70 text-center mt-6 leading-relaxed">
        <Link to="/terms-of-service" className="underline underline-offset-2 hover:text-primary transition-colors">
          {t("settings.terms_of_service")}
        </Link>
        {" · "}
        <Link to="/privacy-policy" className="underline underline-offset-2 hover:text-primary transition-colors">
          {t("settings.privacy_policy")}
        </Link>
      </p>

      <InstallGuideModal
        open={showInstallGuide}
        onOpenChange={setShowInstallGuide}
        onNativeInstall={handleInstallClick}
      />
    </div>
  );
};

export default Login;
